import { useState, type ReactNode } from "react";
import { audio } from "../../lib/audio";
import { dd, crits as mkCrits, type DdCrit } from "./state";
import { Avatar, Btn, Bubble, Choice, Code, Consequence, Diff, FixTag, KeepInstead, MissFooter, MissionHead, MissionResult, Seg, Todo, Win, useGuide, usePrefetch, type DdOpt } from "./ui";
import type { StageProps } from "./types";

type Tone = "risk" | "warn" | "ok" | "info";

/* ================================================================== D4 dependency (S5) */
interface D4Content {
  assistant: string;
  packages: { id: string; name: string; tag: string; tabs: Record<string, string> }[];
  tabs: { id: string; label: string }[];
  judgments: { id: string; label: string; options: DdOpt[]; good: string }[];
  decisions: DdOpt[];
  maya: string;
  crits: { id: string; label: string }[];
}

export function D4({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D4Content;
  const [pkg, setPkg] = useState(c.packages[0].id);
  const [tab, setTab] = useState(c.tabs[0].id);
  const [j, setJ] = useState<Record<string, string>>({});
  const [decision, setDecision] = useState<string>();
  const [stage, setStage] = useState<"review" | "result" | "fix" | "fixed">("review");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [explained, setExplained] = useState(false);
  const [adopted, setAdopted] = useState(false);
  const [fixMsg, setFixMsg] = useState("");
  useGuide(onGuideChange, cfg.guide, stage === "fixed" ? "result" : stage);
  usePrefetch([c.maya]);
  const ready = c.judgments.every((x) => j[x.id]) && !!decision;
  const goodDecision = (d?: string) => d === "existing" || d === "hold";
  const jGood = (x: { id: string; good: string }) => j[x.id] === x.good;

  const submit = () => {
    if (stage === "fix") {
      if (!c.judgments.every(jGood) || !goodDecision(decision)) {
        setFixMsg("Some items still need a fix.");
        audio.ui("error");
        return;
      }
      dd.set({ dependencyBlocker: false });
      dd.log("d4", "Removed the unreviewed dependency; the existing reviewed helper is used.", "corrected");
      audio.ui("success");
      setStage("fixed");
      return;
    }
    const cr = mkCrits(c.crits, {
      identity: jGood(c.judgments[0]),
      version: jGood(c.judgments[1]),
      install: jGood(c.judgments[2]),
      lockfile: jGood(c.judgments[3]),
      decision: goodDecision(decision),
    });
    const kept = dd.commitFirst("d4", cr, c.decisions.find((d) => d.id === decision)?.label ?? "");
    setFirst(kept);
    rec("dd_d4_first", `${dd.missionScore("d4")}/10`);
    if (!goodDecision(decision)) {
      setAdopted(true);
      dd.set({ dependencyBlocker: true });
      dd.log("d4", "An unreviewed dependency was added to candidate R7 (release blocker until reviewed or removed).", "consequence");
      rec("dd_d4_unreviewed", decision);
      audio.ui("error");
    } else audio.ui("success");
    setStage("result");
  };
  const allOk = !!first && first.every((x) => x.ok);
  const cur = c.packages.find((p) => p.id === pkg)!;
  const fix = stage === "fix";

  return (
    <Win app={"Repository review \u00b7 Dependencies"} tag={"S5"} clock={cfg.clock}>
      {(stage === "review" || stage === "fix") && (
        <div className="dd-three">
          <section className="dd-col">
            <Bubble p={cfg.people.assistant} tone="ai">{c.assistant}</Bubble>
            <div className="dd-pkgs">
              {c.packages.map((p) => (
                <button key={p.id} type="button" className={`dd-pkg${pkg === p.id ? " is-on" : ""}`} onClick={() => { audio.click(); setPkg(p.id); }}>
                  <b>{p.name}</b>
                  <small>{p.tag}</small>
                </button>
              ))}
            </div>
          </section>
          <section className="dd-col dd-evid">
            <div className="dd-tabs" role="tablist">
              {c.tabs.map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? "is-on" : ""} onClick={() => { audio.click(); setTab(t.id); }}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="dd-tabbody">
              <span className="dd-kicker">{cur.name}</span>
              <p>{cur.tabs[tab]}</p>
            </div>
            <small className="dd-muted">Package identifiers are training fixtures. Nothing is resolved against a live registry or installed.</small>
            <b className="dd-sub">Decision {fix && !goodDecision(decision) ? <FixTag /> : !decision ? <Todo /> : null}</b>
            <Choice options={c.decisions} value={decision} onChange={setDecision} />
          </section>
          <section className="dd-col">
            <b className="dd-sub">Review card {"\u00b7"} training:rapid-export-helper</b>
            {c.judgments.map((x) => (
              <div key={x.id} className={`dd-judg${fix && !jGood(x) ? " is-fix" : !j[x.id] ? " is-todo" : ""}`}>
                <span>{x.label}{fix && !jGood(x) ? <FixTag /> : !j[x.id] ? <Todo /> : null}</span>
                <Seg small options={x.options} value={j[x.id]} onChange={(v) => setJ((m) => ({ ...m, [x.id]: v }))} />
              </div>
            ))}
            <div className="dd-actions">
              {fixMsg && fix && <span className="dd-warnline">{fixMsg}</span>}
              {fix && <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />}
              <Btn primary pulse={ready} disabled={!ready} onClick={submit}>{fix ? "Submit again" : "Submit dependency decision"}</Btn>
            </div>
          </section>
        </div>
      )}
      {(stage === "result" || stage === "fixed") && first && (
        <MissionResult
          n={4}
          time="10:35"
          title="The package that sounds perfect"
          crits={first}
          outcomes={[
            stage === "fixed"
              ? { tone: "ok", title: "Blocker cleared", body: "The candidate uses the existing reviewed helper. Corrected during training." }
              : adopted
                ? { tone: "risk", title: "Unreviewed dependency added to candidate R7 \u00b7 Release blocker", body: "Its identity cannot be verified and its install hook calls an external host. That is not proof it is malicious, and nothing here claims a compromise. It blocks the release until it is reviewed or removed." }
                : decision === "hold"
                  ? { tone: "ok", title: "Adoption held for dependency review", body: "The story continues with the already approved helper. The review request stays open." }
                  : { tone: "ok", title: "Existing reviewed helper used", body: "No new dependency enters the candidate." },
          ]}
          maya={{ person: cfg.people.maya, text: c.maya, speak: stage === "result" }}
          footer={stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d4", true); finish("continue"); }}>Continue</Btn>
            ) : (
              <MissFooter
                allOk={allOk}
                explained={explained}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d4", false); else dd.acknowledge("d4"); finish("continue"); }}
                retryLabel={adopted ? "Clear the blocker" : "Try again to improve your score"}
              />
            )}
        />
      )}
    </Win>
  );
}

/* ================================================================== D5 authorization (S3 patch, S4 test) */
interface D5Content {
  status: string; handler: string;
  patches: { id: string; label: string; code: string }[];
  checkpoints: { id: string; label: string }[];
  fields: { id: string; label: string; ok: boolean }[];
  users: { id: string; label: string; detail: string }[];
  policy: string; maya: string;
  crits: { id: string; label: string; pts: number }[];
}
const D5_GOOD_FIELDS = (c: D5Content) => c.fields.filter((f) => f.ok).map((f) => f.id).sort().join(",");

function PatchForm({ c, patch, setPatch, cps, setCps, fields, setFields, fix }: {
  c: D5Content; patch?: string; setPatch: (v: string) => void; cps: string[]; setCps: (v: string[]) => void; fields: string[]; setFields: (v: string[]) => void; fix?: boolean;
}) {
  const sel = c.patches.find((p) => p.id === patch);
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const fieldsOk = [...fields].sort().join(",") === D5_GOOD_FIELDS(c);
  return (
    <div className="dd-patchform">
      <b className="dd-sub">1 {"\u00b7"} Patch {fix && patch !== "d" && <FixTag />}</b>
      <Choice options={c.patches.map((p) => ({ id: p.id, label: `${p.id.toUpperCase()}. ${p.label}` }))} value={patch} onChange={setPatch} />
      {sel && <Code small>{sel.code}</Code>}
      <b className="dd-sub">2 {"\u00b7"} Where must this authorization be enforced? {fix && cps.length !== 3 && <FixTag />}</b>
      <div className="dd-toggles">
        {c.checkpoints.map((k) => (
          <button key={k.id} type="button" role="checkbox" aria-checked={cps.includes(k.id)} className={`dd-check${cps.includes(k.id) ? " is-on" : ""}`} onClick={() => { audio.click(); setCps(toggle(cps, k.id)); }}>
            <span className="dd-box" aria-hidden="true">{cps.includes(k.id) ? "\u2713" : ""}</span>{k.label}
          </button>
        ))}
      </div>
      <b className="dd-sub">3 {"\u00b7"} Export fields {fix && !fieldsOk && <FixTag />}</b>
      <div className="dd-toggles">
        {c.fields.map((k) => (
          <button key={k.id} type="button" role="checkbox" aria-checked={fields.includes(k.id)} className={`dd-check is-mono${fields.includes(k.id) ? " is-on" : ""}`} onClick={() => { audio.click(); setFields(toggle(fields, k.id)); }}>
            <span className="dd-box" aria-hidden="true">{fields.includes(k.id) ? "\u2713" : ""}</span>{k.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---- Reusable wizard pieces (tab strip, footer, arrow icon). Other missions reuse these. ---- */
function WizArrow({ dir }: { dir: "left" | "right" }) {
  return (
    <svg className="dd-wz-arrow" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d={dir === "right" ? "M2 8h11M9 4l4 4-4 4" : "M14 8H3M7 4L3 8l4 4"} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface WizStep { id: string; label: string; summary?: string }

/** Numbered tab strip. `reached` = highest step the learner has opened; steps before `active` read as done. */
function WizSteps({ steps, active, reached, onGo }: { steps: WizStep[]; active: number; reached: number; onGo: (i: number) => void }) {
  return (
    <nav className="dd-wz-steps" role="tablist" aria-label="Steps">
      {steps.map((s, i) => {
        const done = i < active;
        const locked = i > reached;
        return (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={i === active}
            disabled={locked}
            className={`dd-wz-step${i === active ? " is-on" : ""}${done ? " is-done" : ""}`}
            onClick={() => { if (i !== active) { audio.click(); onGo(i); } }}
          >
            <span className="dd-wz-num" aria-hidden="true">
              {done ? (
                <svg viewBox="0 0 16 16" width="12" height="12" focusable="false"><path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              ) : i + 1}
            </span>
            <span className="dd-wz-lab">
              <b>{s.label}</b>
              {s.summary && <small>{s.summary}</small>}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function WizFoot({ back, hint, next }: { back?: { label: string; onClick: () => void }; hint?: string; next: ReactNode }) {
  return (
    <footer className="dd-wz-foot">
      <div className="dd-wz-foot-l">
        {back && <button type="button" className="dd-btn" onClick={() => { audio.click(); back.onClick(); }}><WizArrow dir="left" />{back.label}</button>}
      </div>
      <span className="dd-wz-hint">{hint}</span>
      <div className="dd-wz-foot-r">{next}</div>
    </footer>
  );
}

/* ---- Step bodies (same selection UI as PatchForm, separated per step) ---- */
function patchOptions(c: D5Content): DdOpt[] {
  return c.patches.map((p) => ({ id: p.id, label: `${p.id.toUpperCase()}. ${p.label}` }));
}
const toggleId = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

function PatchStep({ c, patch, setPatch }: { c: D5Content; patch?: string; setPatch: (v: string) => void }) {
  const sel = c.patches.find((p) => p.id === patch);
  return (
    <>
      <div className="dd-wz-title">
        <h3>Choose the patch</h3>
        <p>Select one option. You can revise it before applying.</p>
      </div>
      <Choice options={patchOptions(c)} value={patch} onChange={setPatch} />
      <div className="dd-wz-preview">
        <b className="dd-wz-prev-h">Selected patch preview {sel && <span className="dd-wz-pill">{sel.id.toUpperCase()}</span>}</b>
        <Code small>{sel ? sel.code : "No patch selected yet."}</Code>
      </div>
    </>
  );
}

function CheckpointStep({ c, cps, setCps }: { c: D5Content; cps: string[]; setCps: (v: string[]) => void }) {
  return (
    <>
      <div className="dd-wz-title">
        <h3>Where must this authorization be enforced?</h3>
        <p>Select every checkpoint that should run the check.</p>
      </div>
      <div className="dd-toggles dd-wz-list">
        {c.checkpoints.map((k) => (
          <button key={k.id} type="button" role="checkbox" aria-checked={cps.includes(k.id)} className={`dd-check${cps.includes(k.id) ? " is-on" : ""}`} onClick={() => { audio.click(); setCps(toggleId(cps, k.id)); }}>
            <span className="dd-box" aria-hidden="true">{cps.includes(k.id) ? "\u2713" : ""}</span>{k.label}
          </button>
        ))}
      </div>
    </>
  );
}

function FieldsStep({ c, fields, setFields }: { c: D5Content; fields: string[]; setFields: (v: string[]) => void }) {
  return (
    <>
      <div className="dd-wz-title">
        <h3>Choose the export fields</h3>
        <p>Select the fields the export should include.</p>
      </div>
      <div className="dd-toggles dd-wz-list">
        {c.fields.map((k) => (
          <button key={k.id} type="button" role="checkbox" aria-checked={fields.includes(k.id)} className={`dd-check is-mono${fields.includes(k.id) ? " is-on" : ""}`} onClick={() => { audio.click(); setFields(toggleId(fields, k.id)); }}>
            <span className="dd-box" aria-hidden="true">{fields.includes(k.id) ? "\u2713" : ""}</span>{k.label}
          </button>
        ))}
      </div>
    </>
  );
}

export function D5Patch({ cfg, finish, onGuideChange }: StageProps) {
  const c = cfg.content as D5Content;
  const [patch, setPatch] = useState<string>();
  const [cps, setCps] = useState<string[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  useGuide(onGuideChange, cfg.guide, "patch");
  const ready = !!patch && fields.length > 0;
  const who = cfg.people.assistant;
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"} selected`;
  const steps: WizStep[] = [
    { id: "patch", label: "Patch", summary: patch ? `Option ${patch.toUpperCase()} selected` : undefined },
    { id: "auth", label: "Authorization", summary: cps.length ? plural(cps.length, "checkpoint") : undefined },
    { id: "fields", label: "Export fields", summary: fields.length ? plural(fields.length, "field") : undefined },
  ];
  const go = (i: number) => { setStep(i); setReached((r) => Math.max(r, i)); };
  return (
    <Win app={"OrbitIDE \u00b7 Coding assistant"} tag={"S3 \u00b7 IDE"} clock={cfg.clock}>
      <div className="dd-wz">
        <header className="dd-wz-head">
          <span className="dd-wz-kick">Mission 5 of 10 {"\u00b7"} Started 11:15</span>
          <h2>It works for the wrong customer</h2>
        </header>
        <WizSteps steps={steps} active={step} reached={reached} onGo={go} />
        <div className="dd-wz-grid">
          <section className="dd-wz-main">
            {step === 0 && <PatchStep c={c} patch={patch} setPatch={setPatch} />}
            {step === 1 && <CheckpointStep c={c} cps={cps} setCps={setCps} />}
            {step === 2 && <FieldsStep c={c} fields={fields} setFields={setFields} />}
          </section>
          <aside className="dd-wz-ref" aria-label="Reference">
            <h3 className="dd-wz-ref-h">Reference</h3>
            <div className="dd-wz-ai">
              <Avatar p={who} size={34} />
              <div>
                <b>{who.name}</b> <span className="dd-role">{who.role}</span>
                <p>{c.status}</p>
              </div>
            </div>
            <b className="dd-wz-ref-sub">exportHandler {"\u00b7"} Current</b>
            <Code small>{c.handler}</Code>
            <div className="dd-wz-info">
              <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
                <circle cx="8" cy="8" r="7" fill="currentColor" />
                <path d="M8 7v4.2" stroke="#0f1821" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="8" cy="4.6" r="1" fill="#0f1821" />
              </svg>
              <p>{c.policy}</p>
            </div>
          </aside>
        </div>
        {step === 0 && (
          <WizFoot
            hint="Selections stay editable until you apply."
            next={<Btn primary pulse={!!patch} disabled={!patch} onClick={() => go(1)}>Continue to authorization<WizArrow dir="right" /></Btn>}
          />
        )}
        {step === 1 && (
          <WizFoot
            back={{ label: "Back", onClick: () => go(0) }}
            hint="Selections stay editable until you apply."
            next={<Btn primary pulse onClick={() => go(2)}>Continue to export fields<WizArrow dir="right" /></Btn>}
          />
        )}
        {step === 2 && (
          <WizFoot
            back={{ label: "Back", onClick: () => go(1) }}
            hint="Selections stay editable until you apply."
            next={<Btn primary pulse={ready} disabled={!ready} onClick={() => { dd.set({ d5: { patch: patch!, checkpoints: cps, fields } }); audio.ui("success"); finish("continue"); }}>Apply patch</Btn>}
          />
        )}
      </div>
    </Win>
  );
}

const NW_ROWS = [
  ["NW-104", "2026-10-01", "Quarterly Review", "18,240"],
  ["NW-104", "2026-10-08", "Fleet Renewal", "6,975"],
  ["NW-104", "2026-10-15", "Warehouse Audit", "3,410"],
];

export function D5Test({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D5Content;
  const saved: { patch: string; checkpoints: string[]; fields: string[]; fixed?: boolean } = dd.get().d5 ?? { patch: "a", checkpoints: [], fields: [] };
  const [state, setState] = useState(saved);
  const [who, setWho] = useState<string>();
  const [runs, setRuns] = useState<string[]>([]);
  const [last, setLast] = useState<string | null>(null);
  const [stage, setStage] = useState<"test" | "result" | "fix" | "fixed">("test");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [explained, setExplained] = useState(false);
  const [fixMsg, setFixMsg] = useState("");
  const [patch, setPatch] = useState<string | undefined>(saved.patch);
  const [cps, setCps] = useState<string[]>(saved.checkpoints);
  const [fields, setFields] = useState<string[]>(saved.fields);
  useGuide(onGuideChange, cfg.guide, stage === "fixed" ? "result" : stage);
  usePrefetch([c.maya]);
  const secure = state.patch === "d" && state.checkpoints.includes("create");
  const fieldsOk = [...state.fields].sort().join(",") === D5_GOOD_FIELDS(c);

  const run = () => {
    if (!who) return;
    setRuns((r) => [...r, who]);
    setLast(who);
    const leak = who === "hana" && !secure;
    if (leak) {
      rec("dd_d5_cross_tenant", `patch ${state.patch}`);
      dd.log("d5", "Lab check: Harbor's admin received a Northwind export (laboratory result, not production).", "consequence");
      audio.ui("error");
    } else audio.ui("message");
  };
  const result = (u: string, st = state) => {
    const sec = st.patch === "d" && st.checkpoints.includes("create");
    if (u === "nora") return { tone: "info" as Tone, title: "Allowed: Nora exported NW-104", body: "Expected. An allowed case does not test the customer boundary." };
    if (u === "evan") return sec ? { tone: "ok" as Tone, title: "Access denied", body: "Evan is a viewer: no export permission. No record content returned." } : { tone: "risk" as Tone, title: "Allowed: a viewer exported NW-104", body: "Signed in is not the same as permitted to export." };
    return sec ? { tone: "ok" as Tone, title: "Access denied", body: "Hana is not a member of Northwind. No record content and no hint that NW-104 exists." } : { tone: "risk" as Tone, title: "Harbor session shows a Northwind export", body: "Lab result in the synthetic fixture: another customer's data. This did not happen in production." };
  };
  const evaluate = () => {
    const cr = mkCrits(c.crits, {
      patch: state.patch === "d",
      checkpoints: state.checkpoints.length === 3,
      cross: runs[0] === "hana",
      fields: fieldsOk,
    });
    const kept = dd.commitFirst("d5", cr, `Patch ${state.patch.toUpperCase()}, enforced at ${state.checkpoints.length} of 3 checkpoints, first check: ${runs[0]}`);
    setFirst(kept);
    rec("dd_d5_first", `${dd.missionScore("d5")}/10`);
    setStage("result");
  };
  const applyFix = () => {
    const okAll = patch === "d" && cps.length === 3 && [...fields].sort().join(",") === D5_GOOD_FIELDS(c);
    if (!okAll) {
      setFixMsg("Some items still need a fix.");
      audio.ui("error");
      return;
    }
    const st = { patch: "d", checkpoints: cps, fields, fixed: true };
    setState(st);
    dd.set({ d5: st });
    dd.log("d5", "Repaired the boundary: server-side checks at create, job and download; four fields only.", "corrected");
    setLast("hana");
    audio.ui("success");
    setStage("fixed");
  };
  const allOk = !!first && first.every((x) => x.ok);
  const r = last ? result(last) : null;
  const leaking = last === "hana" && !secure;

  return (
    <Win app={"Test lab \u00b7 Synthetic sessions"} tag="S4" clock={cfg.clock}>
      {stage === "test" && (
        <div className="dd-split">
          <aside className="dd-side">
            <MissionHead n={5} time="11:25" title="Test the boundary" sub="Run a check against your patched build." />
            <b className="dd-sub">Which check proves the customer boundary?</b>
            <Choice options={c.users} value={who} onChange={setWho} />
            <div className="dd-actions">
              <Btn primary pulse={!!who && runs.length === 0} disabled={!who} onClick={run}>Run check</Btn>
            </div>
          </aside>
          <section className="dd-main">
            <div className="dd-main-h"><b>Preview</b><span className="dd-muted">Synthetic customers: Northwind Demo, Harbor Demo</span></div>
            {!r && <p className="dd-muted dd-pad">Choose a check and run it.</p>}
            {r && <Consequence tone={r.tone} title={r.title}>{r.body}</Consequence>}
            {leaking && (
              <table className="dd-table">
                <caption>Harbor session {"\u00b7"} NW-104.csv (fictional)</caption>
                <thead><tr><th>report_id</th><th>created_at</th><th>title</th><th>total</th>{!fieldsOk && <th>internal_notes</th>}</tr></thead>
                <tbody>{NW_ROWS.map((row) => <tr key={row[1]}>{row.map((x, i) => <td key={i}>{x}</td>)}{!fieldsOk && <td>Renewal at risk</td>}</tr>)}</tbody>
              </table>
            )}
            {runs.length > 0 && (
              <div className="dd-actions">
                <span className="dd-muted">You can run other checks too.</span>
                <Btn primary pulse onClick={evaluate}>See the result</Btn>
              </div>
            )}
          </section>
        </div>
      )}
      {(stage === "result" || stage === "fixed") && first && (
        <MissionResult
          n={5}
          time="11:40"
          title="It works for the wrong customer"
          crits={first}
          outcomes={[
            stage === "fixed"
              ? { tone: "ok", title: "Re-run: Hana \u2192 NW-104 \u00b7 Access denied", body: "No record content returned. Corrected during training; the earlier lab result stays in your replay." }
              : secure && state.checkpoints.length === 3
                ? { tone: "ok", title: "The boundary holds at create, job and download" }
                : {
                    tone: "warn",
                    title: "The boundary is not complete yet",
                    body: (
                      <>
                        {state.patch !== "d" ? "The chosen patch does not authorize the request on the server. " : ""}
                        {state.checkpoints.length < 3 ? "The job or the download can still bypass the check. " : ""}
                      </>
                    ),
                  },
          ]}
          maya={{ person: cfg.people.maya, text: c.maya, speak: stage === "result" }}
          footer={stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d5", true); finish("continue"); }}>Continue</Btn>
            ) : (
              <MissFooter
                allOk={allOk}
                explained={explained}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d5", false); else dd.acknowledge("d5"); finish("continue"); }}
                retryLabel="Try again to improve your score"
              />
            )}
        />
      )}
      {stage === "fix" && (
        <div className="dd-fixwrap">
          <PatchForm c={c} patch={patch} setPatch={setPatch} cps={cps} setCps={setCps} fields={fields} setFields={setFields} fix />
          <footer className="dd-foot">
            {fixMsg ? <span className="dd-warnline">{fixMsg}</span> : <span className="dd-muted">Applying re-runs the cross-tenant check.</span>}
            <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />
            <Btn primary pulse onClick={applyFix}>Apply and re-run</Btn>
          </footer>
        </div>
      )}
    </Win>
  );
}

/* ================================================================== D6 query + preview (S4) */
interface D6Content {
  assistant: string;
  queries: { id: string; label: string; code: string }[];
  obrien: DdOpt[]; sort: DdOpt[]; render: DdOpt[];
  title: string; maya: string;
  crits: { id: string; label: string; pts: number }[];
}
const D6_GOOD: Record<string, string> = { query: "c", obrien: "literal", sort: "map", render: "text" };

export function D6({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D6Content;
  const [tab, setTab] = useState<"a" | "b" | "c">("a");
  const [a, setA] = useState<Record<string, string>>({});
  const [stage, setStage] = useState<"work" | "result" | "fix" | "fixed">("work");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [explained, setExplained] = useState(false);
  const [committed, setCommitted] = useState<Record<string, string>>({});
  const [fixMsg, setFixMsg] = useState("");
  useGuide(onGuideChange, cfg.guide, stage === "fixed" ? "result" : stage);
  usePrefetch([c.maya]);
  const ready = ["query", "obrien", "sort", "render"].every((k) => a[k]);
  const tabDone = (tab === "a" ? ["query", "obrien"] : tab === "b" ? ["sort"] : ["render"]).every((k) => a[k]);
  const fix = stage === "fix";
  const bad = (k: string) => fix && a[k] !== D6_GOOD[k];
  const commit = () => {
    if (fix) {
      if (Object.keys(D6_GOOD).some((k) => a[k] !== D6_GOOD[k])) {
        setFixMsg("Some answers still need a fix.");
        audio.ui("error");
        return;
      }
      setCommitted({ ...a });
      dd.log("d6", "Bound the query values, fixed the sort mapping and rendered titles as text.", "corrected");
      audio.ui("success");
      setStage("fixed");
      return;
    }
    const cr = mkCrits(c.crits, { bound: a.query === "c", sort: a.sort === "map", render: a.render === "text", valid: a.obrien === "literal" });
    const kept = dd.commitFirst("d6", cr, `Query ${a.query.toUpperCase()}, sort: ${a.sort}, preview: ${a.render}`);
    setFirst(kept);
    setCommitted({ ...a });
    rec("dd_d6_first", `${dd.missionScore("d6")}/10`);
    audio.ui("message");
    setStage("result");
  };
  const allOk = !!first && first.every((x) => x.ok);
  const queryOut = (q: string) =>
    q === "c" ? { tone: "ok" as Tone, t: "Found \u201cO\u2019Brien Quarterly Review\u201d (Northwind only)" } : q === "b" ? { tone: "warn" as Tone, t: "No match: removing apostrophes damaged a valid title" } : { tone: "risk" as Tone, t: "Query error: the apostrophe became query syntax" };
  const preview = (r: string) =>
    r === "inner" ? (
      <span className="dd-pv"><b>Quarterly Review</b><em>Markup interpreted as formatting. The requirement says plain text.</em></span>
    ) : r === "rich" ? (
      <span className="dd-pv"><b>Quarterly Review</b><em>Rich text added: not required here, and a bigger surface to secure.</em></span>
    ) : (
      <span className="dd-pv"><code>{c.title}</code><em>Literal text, exactly as stored.</em></span>
    );

  return (
    <Win app={"Test lab \u00b7 Query and preview"} tag="S4" clock={cfg.clock}>
      {(stage === "work" || fix) && (
        <div className="dd-split">
          <aside className="dd-side">
            <MissionHead n={6} time="12:10" title="A fast query and a clever preview" />
            <Bubble p={cfg.people.assistant} tone="ai">{c.assistant}</Bubble>
            <p className="dd-note">Requirement 4: treat filter values as data, show titles as plain text, allow only known sort fields.</p>
            <div className="dd-tabs is-vertical" role="tablist">
              {([["a", "A \u00b7 Query values", ["query", "obrien"]], ["b", "B \u00b7 Sort field", ["sort"]], ["c", "C \u00b7 Preview", ["render"]]] as [typeof tab, string, string[]][]).map(([id, label, keys]) => (
                <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? "is-on" : ""} onClick={() => { audio.click(); setTab(id); }}>
                  {label} {keys.every((k) => a[k]) ? "\u2713" : ""} {fix && keys.some(bad) ? "\u2022 fix" : ""}
                </button>
              ))}
            </div>
          </aside>
          <section className="dd-main">
            {tab === "a" && (
              <>
                <b className="dd-sub">Which query patch? {bad("query") && <FixTag />}</b>
                <div className="dd-patches">
                  {c.queries.map((q) => (
                    <button key={q.id} type="button" role="radio" aria-checked={a.query === q.id} className={`dd-patch${a.query === q.id ? " is-on" : ""}`} onClick={() => { audio.click(); setA((m) => ({ ...m, query: q.id })); }}>
                      <b>{q.label}</b>
                      <Code small>{q.code}</Code>
                    </button>
                  ))}
                </div>
                <b className="dd-sub">A user searches for the title O{"\u2019"}Brien Quarterly Review. What should happen? {bad("obrien") && <FixTag />}</b>
                <Seg small options={c.obrien} value={a.obrien} onChange={(v) => setA((m) => ({ ...m, obrien: v }))} />
              </>
            )}
            {tab === "b" && (
              <>
                <b className="dd-sub">The UI offers Sort by date and Sort by title. How does the server handle sortField? {bad("sort") && <FixTag />}</b>
                <Choice options={c.sort} value={a.sort} onChange={(v) => setA((m) => ({ ...m, sort: v }))} />
              </>
            )}
            {tab === "c" && (
              <>
                <b className="dd-sub">The fixture title is <code>{c.title}</code>. The draft uses preview.innerHTML = report.title. {bad("render") && <FixTag />}</b>
                <Choice options={c.render} value={a.render} onChange={(v) => setA((m) => ({ ...m, render: v }))} />
              </>
            )}
            <div className="dd-actions">
              {fixMsg && fix && <span className="dd-warnline">{fixMsg}</span>}
              {tab !== "c" && <Btn primary={tabDone && !ready} pulse={tabDone && !ready} onClick={() => setTab(tab === "a" ? "b" : "c")}>Next part</Btn>}
              {fix && <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />}
              <Btn primary pulse={ready} disabled={!ready} onClick={commit}>Commit changes</Btn>
            </div>
          </section>
        </div>
      )}
      {(stage === "result" || stage === "fixed") && first && (
        <MissionResult
          n={6}
          time="12:25"
          title="Before and after, in the test lab"
          crits={first}
          outcomeLabel="Before and after"
          outcomeSub="Search result and preview"
          outcomes={stage === "fixed" ? [{ tone: "ok", title: "Corrected during training", body: "The repaired state carries into the next tests." }] : undefined}
          more={
            <div className="dd-compare">
              <div>
                <span className="dd-kicker">Search: O{"\u2019"}Brien Quarterly Review</span>
                <Consequence tone={queryOut(committed.query).tone} title={queryOut(committed.query).t} />
              </div>
              <div>
                <span className="dd-kicker">Preview of {c.title}</span>
                <div className="dd-pvbox">{preview(committed.render)}</div>
              </div>
            </div>
          }
          maya={{ person: cfg.people.maya, text: c.maya, speak: stage === "result" }}
          footer={stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d6", true); finish("continue"); }}>Continue</Btn>
            ) : (
              <MissFooter
                allOk={allOk}
                explained={explained}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d6", false); else dd.acknowledge("d6"); finish("continue"); }}
                retryLabel="Try again to improve your score"
              />
            )}
        />
      )}
    </Win>
  );
}

/* ================================================================== D7 diff review (S5) */
interface D7Content {
  summary: string;
  files: { id: string; name: string; good: string; diff: string[]; note: string; reason?: string }[];
  reasons: DdOpt[];
  misses: Record<string, string>;
  maya: string;
  crits: { id: string; label: string }[];
}
const DECIDE: DdOpt[] = [{ id: "keep", label: "Keep" }, { id: "reject", label: "Reject" }, { id: "refer", label: "Refer for review" }];

export function D7({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D7Content;
  const [sel, setSel] = useState<string | null>(null);
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const [dec, setDec] = useState<Record<string, string>>({});
  const [why, setWhy] = useState<Record<string, string>>({});
  const [stage, setStage] = useState<"review" | "result" | "fix" | "fixed">("review");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [explained, setExplained] = useState(false);
  const [missed, setMissed] = useState<string[]>([]);
  const [fixMsg, setFixMsg] = useState("");
  useGuide(onGuideChange, cfg.guide, stage === "fixed" ? "result" : stage);
  usePrefetch([c.maya]);
  const good = (f: D7Content["files"][number]) => (f.good === "keep" ? dec[f.id] === "keep" : dec[f.id] === "reject" || dec[f.id] === "refer");
  const decided = (f: D7Content["files"][number]) => !!dec[f.id] && (dec[f.id] === "keep" || !!why[f.id]);
  const ready = c.files.every(decided);
  const fix = stage === "fix";
  const f = c.files.find((x) => x.id === sel);

  const submit = () => {
    if (fix) {
      if (!c.files.every(good)) {
        setFixMsg("Some files still need a fix.");
        audio.ui("error");
        return;
      }
      dd.log("d7", "Restored the security assertion and the CI gate, removed the agent-rule change and the unreviewed package.", "corrected");
      audio.ui("success");
      setStage("fixed");
      return;
    }
    const by = Object.fromEntries(c.files.map((x) => [x.id, good(x)]));
    const cr = mkCrits(c.crits, { keep: by.preview && by.readme, test: by.test, ci: by.ci, agents: by.agents, lock: by.lock });
    const kept = dd.commitFirst("d7", cr, c.files.map((x) => `${x.name.split("/").pop()}: ${dec[x.id]}`).join(", "));
    setFirst(kept);
    rec("dd_d7_first", `${dd.missionScore("d7")}/10`);
    const miss = c.files.filter((x) => !good(x)).map((x) => x.id);
    setMissed(miss);
    if (miss.includes("test")) {
      rec("dd_d7_weak_test", "accepted toExist()");
      dd.log("d7", "Accepted a weakened cross-tenant assertion (shown with a flawed demonstration fixture).", "consequence");
    }
    audio.ui(miss.length ? "error" : "success");
    setStage("result");
  };
  const allOk = !!first && first.every((x) => x.ok);

  return (
    <Win app={"Repository review \u00b7 Pull request #219"} tag="S5" clock={cfg.clock}>
      {(stage === "review" || fix) && (
        <div className="dd-split is-wide">
          <aside className="dd-side">
            <MissionHead n={7} time="13:30" title="All tests are green" />
            <Bubble p={cfg.people.assistant} tone="ai">{c.summary}</Bubble>
            <div className="dd-ci-ok">{"\u2713"} CI passed {"\u00b7"} 6 files changed</div>
            <ul className="dd-files">
              {c.files.map((x) => (
                <li key={x.id}>
                  <button type="button" className={`dd-file${sel === x.id ? " is-on" : ""}${fix && !good(x) ? " is-fix" : !decided(x) ? " is-todo" : ""}`} onClick={() => { audio.click(); setSel(x.id); setOpened((o) => ({ ...o, [x.id]: true })); }}>
                    <span className="dd-mono">{x.name}</span>
                    {fix && !good(x) ? <FixTag /> : decided(x) ? <small className="dd-ok-s">{DECIDE.find((d) => d.id === dec[x.id])?.label}</small> : <Todo label={opened[x.id] ? "Decide" : "Open"} />}
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <section className="dd-main">
            {!f ? (
              <p className="dd-muted dd-pad">Open a changed file to review it. The badge is green; the question is what the changes actually do.</p>
            ) : (
              <>
                <div className="dd-main-h"><b className="dd-mono">{f.name}</b></div>
                <Diff lines={f.diff} />
                <b className="dd-sub">Your decision</b>
                <Seg options={DECIDE} value={dec[f.id]} onChange={(v) => setDec((m) => ({ ...m, [f.id]: v }))} />
                {dec[f.id] && dec[f.id] !== "keep" && (
                  <>
                    <b className="dd-sub">Reason</b>
                    <Seg small options={c.reasons} value={why[f.id]} onChange={(v) => setWhy((m) => ({ ...m, [f.id]: v }))} />
                    {why[f.id] === "ai" && <span className="dd-warnline">Who wrote a change is not a reason by itself. Judge what it does.</span>}
                  </>
                )}
              </>
            )}
            <div className="dd-actions">
              {fixMsg && fix && <span className="dd-warnline">{fixMsg}</span>}
              <span className={`dd-decided${ready ? " is-all" : ""}`}><b>{c.files.filter(decided).length}</b> of {c.files.length} decided{ready ? " \u2713" : ""}</span>
              {fix && <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />}
              <Btn primary pulse={ready} disabled={!ready} onClick={submit}>{fix ? "Submit again" : "Submit review"}</Btn>
            </div>
          </section>
        </div>
      )}
      {(stage === "result" || stage === "fixed") && first && (
        <MissionResult
          n={7}
          time="13:50"
          title="What your review decided"
          crits={first}
          outcomes={
            stage === "fixed"
              ? [{ tone: "ok", title: "Required assertion and CI gate restored", body: "Agent rules unchanged, unreviewed package removed, useful changes kept. Corrected during training." }]
              : missed.length
                ? missed.map((m) => ({ tone: m === "preview" || m === "readme" ? ("info" as const) : ("risk" as const), title: c.files.find((x) => x.id === m)!.name, body: c.misses[m] }))
                : [{ tone: "ok", title: "Useful changes kept, unsafe ones rejected", body: "The layout fix and the documentation ship; the security requirement stays tested." }]
          }
          maya={{ person: cfg.people.maya, text: c.maya, speak: stage === "result" }}
          footer={stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d7", true); finish("continue"); }}>Continue</Btn>
            ) : (
              <MissFooter
                allOk={allOk}
                explained={explained}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d7", false); else dd.acknowledge("d7"); finish("continue"); }}
                retryLabel="Try again to improve your score"
              />
            )}
        />
      )}
    </Win>
  );
}
